import React, { useContext, useState } from 'react';
import { Grid, Typography, Paper, Button, IconButton, Avatar } from '@material-ui/core';
import LocalMallOutlinedIcon from '@material-ui/icons/LocalMallOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { I18nContext } from 'translations';
import { formatCostTemporal } from 'utils';
import { SIZE_CD } from 'utils/constants';
import { packageStatusLabel, paymentMethodLabel } from 'utils/deliveryRoute';
import PackageStatusChip from './PackageStatusChip';
import RelaunchedChip from './RelaunchedChip';
import PackageProgress from './PackageProgress';
import CustomerBlock from './CustomerBlock';

// A label/value line styled as a small table row. `label` is rendered as its own text node
// so tests can still query it verbatim (e.g. "app.booking.declaredValue:") the way they did
// before this row layout existed.
function FieldRow({ label, value }) {
  return (
    <Grid
      container
      justify="space-between"
      alignItems="center"
      style={{ padding: '7px 0', borderBottom: '1px solid #F2EFF5' }}
    >
      <Grid item>
        <Typography variant="caption" color="textSecondary" style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {label}
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant="body2" color="textPrimary" style={{ fontWeight: 700 }}>
          {value}
        </Typography>
      </Grid>
    </Grid>
  );
}

export default function PackageCard({ pkg, onFocusStop, showCompany, stopAction }) {
  const { translate } = useContext(I18nContext);
  const [showDeliveryCode, setShowDeliveryCode] = useState(false);

  return (
    <Paper
      elevation={0}
      className="delivery-route-package-card"
      style={{ padding: 16, marginBottom: 12, border: '1px solid #EDEAF0' }}
      data-testid="package-card"
    >
      <Grid container justify="space-between" alignItems="center">
        <Grid item>
          <Grid container alignItems="center" wrap="nowrap" spacing={1}>
            <Grid item>
              <Avatar style={{ backgroundColor: '#EFE3FB', color: '#7825bd', width: 32, height: 32 }}>
                <LocalMallOutlinedIcon fontSize="small" />
              </Avatar>
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="textPrimary" style={{ fontWeight: 700 }}>
                {`${translate('app.deliveryRoute.packageNumber')} ${pkg.number}`}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            {pkg.relaunched && (
              <Grid item>
                <RelaunchedChip />
              </Grid>
            )}
            <Grid item>
              <PackageStatusChip status={pkg.status} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <PackageProgress progress={pkg.progress} />

      <div style={{ marginTop: 8 }}>
        {showCompany && pkg.company_name && (
          <FieldRow label={`${translate('app.booking.company')}:`} value={pkg.company_name} />
        )}

        <FieldRow label={`${translate('app.booking.orderNumber')}:`} value={<b>{pkg.reference}</b>} />

        {SIZE_CD?.[pkg.size_cd] && (
          <FieldRow label={`${translate('app.booking.packageSize')}:`} value={translate(SIZE_CD[pkg.size_cd])} />
        )}

        <FieldRow label={`${translate('app.deliveryRoute.status')}:`} value={<b>{packageStatusLabel(pkg.status, translate)}</b>} />

        {pkg.indications && (
          <FieldRow label={`${translate('app.booking.indications')}:`} value={pkg.indications} />
        )}

        {pkg.counter_delivery && pkg.collected_value && (
          <FieldRow
            label={`${translate('app.deliveryRoute.collectedValue')}:`}
            value={formatCostTemporal(pkg.collected_value)}
          />
        )}

        {pkg.requires_a_driver_with_base_money && pkg.money_to_pay_for_package && (
          <FieldRow
            label={`${translate('app.deliveryRoute.baseRequired')}:`}
            value={formatCostTemporal(pkg.money_to_pay_for_package)}
          />
        )}

        {/* company_charge is what the company is billed (commission included); delivery_cost is the
            driver-facing net for wallet/card payments, so it only stands in for payloads without it. */}
        {(pkg.payment?.company_charge || pkg.payment?.delivery_cost) && (
          <FieldRow
            label={`${translate('app.deliveryRoute.deliveryPayment')}:`}
            value={formatCostTemporal(pkg.payment.company_charge || pkg.payment.delivery_cost)}
          />
        )}

        {(pkg.payment?.method_cd || pkg.payment?.method) && (
          <FieldRow
            label={`${translate('app.booking.paymentMethod')}:`}
            value={paymentMethodLabel(pkg.payment, translate)}
          />
        )}

        {pkg.declared_value?.subunits > 0 && (
          <FieldRow
            label={`${translate('app.booking.declaredValue')}:`}
            value={formatCostTemporal(pkg.declared_value)}
          />
        )}

        {pkg.validation_code && (
          <Grid container justify="space-between" alignItems="center" style={{ padding: '7px 0', borderBottom: '1px solid #F2EFF5' }}>
            <Grid item>
              <Typography variant="caption" color="textSecondary" style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}>
                {`${translate('app.booking.deliveryCode')}:`}
              </Typography>
            </Grid>
            <Grid item>
              <Grid container alignItems="center" spacing={0}>
                <Grid item>
                  <Typography variant="body2" color="textPrimary" style={{ fontWeight: 700 }}>
                    {showDeliveryCode ? pkg.validation_code : '••••'}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton
                    size="small"
                    data-testid="delivery-code-toggle"
                    aria-label={translate(showDeliveryCode ? 'app.deliveryRoute.hideDeliveryCode' : 'app.deliveryRoute.showDeliveryCode')}
                    onClick={() => setShowDeliveryCode((prev) => !prev)}
                  >
                    {showDeliveryCode ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </div>

      {pkg.stop?.address && (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          {pkg.stop.address}
        </Typography>
      )}

      {stopAction !== 'dropoff' && (
        <CustomerBlock
          customer={pkg.origin_contact}
          labelKey="app.deliveryRoute.originContactLabel"
          captionKey="app.deliveryRoute.handsOverPackage"
          testId="origin-contact-block"
        />
      )}

      {stopAction !== 'pickup' && <CustomerBlock customer={pkg.customer} />}

      {pkg.stop && onFocusStop && (
        <Button size="small" color="primary" onClick={() => onFocusStop(pkg.stop.id)} style={{ marginTop: 8 }}>
          {translate('app.deliveryRoute.viewStop')}
        </Button>
      )}
    </Paper>
  );
}
